import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Product } from '../types'

const PAGE_SIZE = 1000

/** Produto inativo enriquecido com a data da última venda (view last_sale_per_product). */
export type InactiveProduct = Product & { last_sale_at: string | null }

export function useInactiveProducts() {
  const [products, setProducts] = useState<InactiveProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function fetchInactive() {
    try {
      // 1) Produtos inativos (paginado)
      const all: Product[] = []
      let from = 0
      while (true) {
        const { data, error: err } = await supabase
          .from('products')
          .select('*, suppliers(*)')
          .eq('is_active', false)
          .is('deleted_at', null)
          .order('name')
          .range(from, from + PAGE_SIZE - 1)
        if (err) throw err
        if (!data || data.length === 0) break
        all.push(...(data as Product[]))
        if (data.length < PAGE_SIZE) break
        from += PAGE_SIZE
      }

      // 2) Última venda por produto (view agregada — evita transferir todas as vendas)
      const { data: salesData, error: salesErr } = await supabase
        .from('last_sale_per_product')
        .select('product_id, last_sale_date')
      if (salesErr) throw salesErr
      const lastSaleByProduct = new Map<string, string>()
      for (const s of (salesData ?? []) as Array<{ product_id: string; last_sale_date: string }>) {
        lastSaleByProduct.set(s.product_id, s.last_sale_date)
      }

      const enriched: InactiveProduct[] = all.map(p => ({
        ...p,
        last_sale_at: lastSaleByProduct.get(p.id) ?? null,
      }))
      setProducts(enriched)
      setLoading(false)
    } catch (e) {
      setError((e as Error).message)
      setLoading(false)
    }
  }

  async function reactivate(productId: string) {
    const { error: err } = await supabase
      .from('products')
      .update({ is_active: true })
      .eq('id', productId)
    if (err) throw err
    setProducts(prev => prev.filter(p => p.id !== productId))
  }

  async function inactivate(productId: string) {
    const { error: err } = await supabase
      .from('products')
      .update({ is_active: false })
      .eq('id', productId)
    if (err) throw err
  }

  /**
   * Exclui definitivamente (soft-delete). O registro permanece no DB para
   * auditoria e para preservar FKs em stock_movements/sales/purchase_order_items,
   * mas some de todas as telas (todos os hooks de listagem filtram deleted_at IS NULL).
   */
  async function softDelete(productId: string) {
    const { error: err } = await supabase
      .from('products')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', productId)
    if (err) throw err
    setProducts(prev => prev.filter(p => p.id !== productId))
  }

  useEffect(() => {
    // Realtime de `products` removido: a sync diária do Tiny mexe em ~900 produtos de uma
    // vez, disparando refetch COMPLETO da lista de inativos em cascata (egress alto). O
    // refetch no mount já cobre; o usuário pode atualizar manualmente (refresh exposto pelo hook).
    fetchInactive()
  }, [])

  return { products, loading, error, reactivate, inactivate, softDelete, refresh: fetchInactive }
}
